class UGCCarouselWithExpand extends HTMLElement {
  constructor() {
    super();
    this.siteVideoEls = null;
    this.onVideoStateChange = this.onVideoStateChange.bind(this);
    this.scrollPrev = this.scrollPrev.bind(this);
    this.scrollNext = this.scrollNext.bind(this);
    this.scrollToPage = this.scrollToPage.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.container = null;
    this.prevButtons = null;
    this.nextButtons = null;
    this.pager = null;
    this.scrollDistance = 0;
    this.animationSpeed = 250;
    this.bulletsQuantity = parseInt(this.dataset.bulletsQuantity, 10) || 1;
    this._scrollTicking = false;
  }

  connectedCallback() {
    this.siteVideoEls = this.querySelectorAll("site-video video");
    this.container = this.querySelector(".ugc-container");
    this.prevButtons = this.querySelectorAll(".prev");
    this.nextButtons = this.querySelectorAll(".next");
    this.pager = this.querySelector(".pager");

    this.calculateScrollDistance();
    this.addEventListeners();
    this.updateButtonState();
    this.updatePager();

    this.toggleExpandButtons = this.querySelectorAll(".toggle-expand-js");

    if (this.toggleExpandButtons.length > 0) {
      this.toggleExpandButtons.forEach((button, index) => {
        button.addEventListener("click", () => {
          const isAlreadyExpanded = this.querySelector(".expanded-card");
          if (isAlreadyExpanded) {
            setTimeout(() => {
              this.onResize();
              this.scrollToPage(index);
            }, 350);
          } else {
            if (index + 1 === this.toggleExpandButtons.length) {
              setTimeout(() => {
                this.onResize();
                this.scrollToPage(index);
              }, 301);
              this.scrollToPage(index);
            } else {
              setTimeout(() => {
                this.onResize();
                this.scrollToPage(index);
              }, 350);
            }
          }
        });
      });
    } else {
      console.warn("No .toggle-expand-js Button");
    }
  }

  addEventListeners() {
    if (this.prevButtons) {
      this.prevButtons.forEach((prevButton) => {
        prevButton.addEventListener("click", this.scrollPrev);
      });
    }

    if (this.nextButtons) {
      this.nextButtons.forEach((nextButton) => {
        nextButton.addEventListener("click", this.scrollNext);
      });
    }

    if (this.pager) {
      this.pager.querySelectorAll("button").forEach((dot, index) => {
        dot.addEventListener("click", () => {
          this.scrollToPage(index);
        });
      });
    }

    this.container.addEventListener("scroll", this.onScroll);
    window.addEventListener("resize", this.onResize);
  }

  onVideoStateChange(index) {
    this.siteVideoEls.forEach((video, i) => {
      if (i === index) {
        video.play();
      } else {
        video.pause();
      }
    });
  }

  scrollPrev() {
    const currentPage = this.getCurrentPage();
    const targetPage = Math.max(currentPage - 1, 0);
    this.scrollToPage(targetPage);
  }

  scrollNext() {
    const currentPage = this.getCurrentPage();
    const totalPages = this.getTotalPages();
    const targetPage = Math.min(currentPage + 1, totalPages - 1);
    this.scrollToPage(targetPage);
  }

  scrollToPage(index) {
    const targetScrollLeft = index * this.scrollDistance;
    this.smoothScrollTo(targetScrollLeft);
    this.updateButtonState();
    this.updatePager(index);
    setTimeout(() => {
      this.onVideoStateChange(index);
    }, 50);
  }

  smoothScrollTo(targetScrollLeft) {
    const start = this.container.scrollLeft;
    const distance = targetScrollLeft - start;
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / this.animationSpeed, 1);
      this.container.scrollLeft = start + distance * progress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  onScroll() {
    if (this._scrollTicking) return;
    this._scrollTicking = true;
    requestAnimationFrame(() => {
      this.calculateScrollDistance();
      this.updateButtonState();
      this.updatePager();
      this._scrollTicking = false;
    });
  }

  updateButtonState() {
    const maxScrollLeft =
      this.container.scrollWidth - this.container.clientWidth;

    this.prevButtons.forEach((prevButton) => {
      prevButton.disabled = this.container.scrollLeft <= 0;
    });

    this.nextButtons.forEach((nextButton) => {
      nextButton.disabled = this.container.scrollLeft >= maxScrollLeft;
    });
  }

  updatePager(selectedIndex = null) {
    const currentPage =
      selectedIndex !== null ? selectedIndex : this.getCurrentPage();

    if (this.pager) {
      this.pager.querySelectorAll("button").forEach((dot, index) => {
        dot.classList.toggle("active", index === currentPage);
      });
    }
    this.onVideoStateChange(currentPage);
  }

  getCurrentPage() {
    const maxPages = Math.min(this.getTotalPages(), this.bulletsQuantity);
    if (this.container.scrollLeft === 0) {
      return 0;
    }

    const maxScrollLeft =
      this.container.scrollWidth - this.container.clientWidth;
    if (this.container.scrollLeft >= maxScrollLeft) {
      return maxPages - 1;
    }

    return Math.min(
      Math.round(this.container.scrollLeft / this.scrollDistance),
      maxPages - 1
    );
  }

  getTotalPages() {
    return Math.ceil(this.container.scrollWidth / this.scrollDistance);
  }

  calculateScrollDistance() {
    this.scrollDistance =
      (this.container.scrollWidth - this.container.clientWidth) /
      (this.bulletsQuantity - 1);
  }

  onResize() {
    this.calculateScrollDistance();
    this.updateButtonState();
    this.updatePager();
  }
}

customElements.define("ugc-carousel-with-expand", UGCCarouselWithExpand);

class ExpandCardUgc extends HTMLElement {
  connectedCallback() {
    this.toggleExpand = this.querySelector(".toggle-expand-js");
    this.allExpandCardContainers = this.querySelectorAll(
      ".expand-card-container"
    );
    this.allExpandCardContainersIncludingOutComponent =
      document.querySelectorAll(".expand-card-container");

    if (this.toggleExpand) {
      this.toggleExpandCard();
    }
    this.allExpandCardContainersIncludingOutComponent.forEach((container) => {
      container.classList.remove("expanded-card");
    });
  }

  toggleExpandCard() {
    this.toggleExpand.addEventListener("click", () => {
      const isMobile = window.innerWidth < 768;
      const isAlreadyExpanded =
        this.allExpandCardContainers[0]?.classList.contains("expanded-card");

      this.allExpandCardContainersIncludingOutComponent.forEach((container) => {
        container.classList.remove("expanded-card");
      });

      if (isMobile) {
        this.allExpandCardContainersIncludingOutComponent.forEach(
          (container) => {
            container.classList.toggle("expanded-card-mobile");
          }
        );
      } else {
        if (!isAlreadyExpanded) {
          this.allExpandCardContainers.forEach((container) => {
            container.classList.add("expanded-card");
          });
        }
      }
    });
  }
}

customElements.define("expand-card-ugc", ExpandCardUgc);
